/**
 * Admin Moderation Service (US-12-GR-06)
 *
 * API client for the admin comment moderation endpoints.
 * Uses the existing authenticatedFetch from src/api/ for automatic
 * Bearer token injection.
 *
 * Routing (post-verify fix, superseding US-12-GR-12):
 *   The admin SWA builds with VITE_API_URL pointed directly at TaskManager.API
 *   (not the ChellusMobileGateway), so TaskManager has no `/api/journal/*`
 *   routes to rewrite and every moderation call 404'd live. The Journals
 *   service actually serves these routes directly, WITHOUT a `/journal`
 *   prefix — that prefix was only ever a gateway-only rewrite
 *   (`/api/journal/{everything}` → `/api/…` on the Journals.API host).
 *
 *   Mirrors the per-service base-URL pattern in agentAdminService.ts
 *   (VITE_SERVICEMANAGER_API_URL / SERVICEMANAGER_BASE): when
 *   VITE_JOURNALS_API_URL is set (all deployed builds), requests go straight
 *   to the Journals.API host at `/api/v1/admin/comments/...`. When unset
 *   (local dev), falls back to the relative `/api/journal/v1/admin/comments/...`
 *   path, which the Vite dev proxy / gateway rewrites as before.
 *
 * Endpoints (all admin-only, require Admin role in JWT):
 *   GET  /api/v1/admin/comments/reported             — paginated reported list
 *   POST /api/v1/admin/comments/reported/{id}/resolve — resolve with outcome
 *   POST /api/v1/admin/comments/{id}/hide            — hide a comment
 *   POST /api/v1/admin/comments/{id}/unhide          — unhide a comment
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import type { ReportOutcome, ReportStatus, ReportedCommentRow } from '../types/comment';

const JOURNALS_BASE: string = import.meta.env.VITE_JOURNALS_API_URL || '';

const ADMIN_COMMENTS = JOURNALS_BASE
  ? `${JOURNALS_BASE}/api/v1/admin/comments`
  : '/api/journal/v1/admin/comments';

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

interface ApiResult<T> {
  data: T;
  success: boolean;
  error?: string;
}

async function callApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  try {
    const response = await authenticatedFetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers ?? {}),
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      return {
        data: null as T,
        success: false,
        error: (body['message'] as string | undefined) ?? `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { data: null as T, success: true };
    }

    const data = await response.json() as T;
    return { data, success: true };
  } catch (err) {
    return {
      data: null as T,
      success: false,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}

// ---------------------------------------------------------------------------
// Response normalisation
// ---------------------------------------------------------------------------

/** Page size assumed when the caller does not name one (mirrors the API default). */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Both shapes GET .../admin/comments/reported has been observed to return.
 *
 * Journals.API declares `IEnumerable<ReportedCommentResponse>` and serialises a
 * BARE ARRAY, even though it accepts pageIndex/pageSize and its own doc comment
 * says "paginated". Reading `.items` off that array yields undefined, and the
 * page then evaluates `state.rows.length` on undefined and renders nothing at
 * all. Measured against dev-chelless-journals 2026-08-25.
 *
 * The envelope arm is kept so this keeps working unchanged once the API returns
 * one, at which point totalCount becomes authoritative again.
 */
export type ReportedCommentsPayload =
  | ReportedCommentRow[]
  | { items: ReportedCommentRow[]; totalCount: number };

/**
 * Collapses either shape to the envelope the page consumes.
 *
 * For a bare array the server sends no grand total, so the best honest figure is
 * how far the caller has already paged plus what this page returned. A full page
 * therefore leaves "Next" disabled: the client genuinely cannot tell whether more
 * rows exist. That limitation is the API's to remove by returning a real
 * totalCount - it is deliberately not guessed at here.
 */
export function normaliseReportedComments(
  payload: ReportedCommentsPayload,
  pageIndex: number,
  pageSize: number
): { items: ReportedCommentRow[]; totalCount: number } {
  if (Array.isArray(payload)) {
    return { items: payload, totalCount: pageIndex * pageSize + payload.length };
  }
  return { items: payload.items ?? [], totalCount: payload.totalCount ?? 0 };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve paginated reported comments.
 * GET /api/admin/comments/reported?status=&pageIndex=&pageSize=
 */
export async function getReportedComments(opts: {
  status?: ReportStatus;
  pageIndex?: number;
  pageSize?: number;
}): Promise<{ items: ReportedCommentRow[]; totalCount: number }> {
  const params = new URLSearchParams();
  if (opts.status !== undefined) params.set('status', opts.status);
  if (opts.pageIndex !== undefined) params.set('pageIndex', String(opts.pageIndex));
  if (opts.pageSize !== undefined) params.set('pageSize', String(opts.pageSize));

  const qs = params.toString();
  const url = `${ADMIN_COMMENTS}/reported${qs ? `?${qs}` : ''}`;

  const result = await callApi<ReportedCommentsPayload>(url);
  if (!result.success || !result.data) {
    throw new Error(result.error ?? 'Failed to fetch reported comments');
  }
  return normaliseReportedComments(
    result.data,
    opts.pageIndex ?? 0,
    opts.pageSize ?? DEFAULT_PAGE_SIZE
  );
}

/**
 * Resolve a report with an outcome decision.
 * POST /api/admin/comments/reported/{reportId}/resolve
 */
export async function resolveReport(reportId: string, outcome: ReportOutcome): Promise<void> {
  const result = await callApi<void>(`${ADMIN_COMMENTS}/reported/${reportId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ Outcome: outcome }),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to resolve report');
  }
}

/**
 * Hide a comment (admin only, reversible).
 * POST /api/admin/comments/{commentId}/hide
 */
export async function hideComment(commentId: number): Promise<void> {
  const result = await callApi<void>(`${ADMIN_COMMENTS}/${commentId}/hide`, { method: 'POST' });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to hide comment');
  }
}

/**
 * Unhide a previously hidden comment (admin only).
 * POST /api/admin/comments/{commentId}/unhide
 */
export async function unhideComment(commentId: number): Promise<void> {
  const result = await callApi<void>(`${ADMIN_COMMENTS}/${commentId}/unhide`, { method: 'POST' });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to unhide comment');
  }
}

export const adminModerationService = {
  getReportedComments,
  resolveReport,
  hideComment,
  unhideComment,
};
